import { type DivIcon, LatLng } from 'leaflet';
import { cn } from 'noddi-ui-common';
import { useIsMobile } from 'noddi-ui-web';
import { useEffect, useRef, useState } from 'react';
import { Marker, useMapEvents } from 'react-leaflet';
import type { MapLayerKey } from './constants';
import { getNoddiLeafletIcon } from './markers/getNoddiLeafletIcon';
import { NoddiLeafletMapContainer } from './NoddiLeafletMapContainer';

interface LocationPickerProps {
  latitude?: number;
  longitude?: number;
  onLocationChange?: (latitude: number, longitude: number) => void;
  defaultLayer?: MapLayerKey;
  /** Override the center (selected) marker. Defaults to the blue map pin. */
  markerIcon?: DivIcon;
  children?: React.ReactNode;
}

export const LocationPicker = ({
  latitude,
  longitude,
  onLocationChange,
  defaultLayer,
  markerIcon,
  children
}: LocationPickerProps) => {
  const isMobile = useIsMobile();
  const [centerPosition, setCenterPosition] = useState<LatLng | undefined>(
    latitude && longitude ? new LatLng(latitude, longitude) : undefined
  );
  const selectedIcon = markerIcon ?? getNoddiLeafletIcon();

  return (
    // Map pin is in centre of view, so if the vh is too tall on mobile, you will not see the pin,
    // So need to override the default 600px height of the container, as this pin is in the dialog..
    <NoddiLeafletMapContainer initialZoom={20} defaultLayer={defaultLayer} className={cn(isMobile && 'h-[40vh]')}>
      <MapCenterWatcher
        initialPosition={centerPosition}
        onCenterChange={(position) => {
          setCenterPosition(position);
          onLocationChange?.(position.lat, position.lng);
        }}
      />
      {centerPosition && <Marker position={centerPosition} icon={selectedIcon} interactive={false} />}
      {children}
    </NoddiLeafletMapContainer>
  );
};

interface MapCenterWatcherProps {
  initialPosition?: LatLng;
  onCenterChange: (position: LatLng) => void;
}

const MapCenterWatcher = ({ initialPosition, onCenterChange }: MapCenterWatcherProps) => {
  const callbackRef = useRef(onCenterChange);
  useEffect(() => {
    callbackRef.current = onCenterChange;
  }, [onCenterChange]);

  const isZoomingRef = useRef(false);
  const lastCenterRef = useRef<LatLng | undefined>(undefined);

  const map = useMapEvents({
    zoomstart: () => {
      isZoomingRef.current = true;
    },
    zoomend: () => {
      isZoomingRef.current = false;
    },
    move: () => {
      if (isZoomingRef.current) {
        return;
      } // keep coords stable during zoom

      const center = map.getCenter();
      if (!lastCenterRef.current || !lastCenterRef.current.equals(center)) {
        lastCenterRef.current = center;
        callbackRef.current(center);
      }
    }
  });

  useEffect(() => {
    const shouldCenter = initialPosition && (!lastCenterRef.current || !lastCenterRef.current.equals(initialPosition));

    if (shouldCenter && initialPosition) {
      lastCenterRef.current = initialPosition;
      map.setView(initialPosition, map.getZoom(), { animate: false });
      callbackRef.current(initialPosition);
      return;
    }

    if (!lastCenterRef.current && !initialPosition) {
      const center = map.getCenter();
      lastCenterRef.current = center;
      callbackRef.current(center);
    }
  }, [initialPosition, map]);

  return null;
};
